import {
  getLookupQueryKey,
  prefetchLookup,
  useLookup,
} from '@unifyapps/network/generated/lookup-rest-api/lookup-rest-api';
import type { CurrentUserDetails } from '@unifyapps/network/generated/models/currentUserDetails';
import type { QueryClient } from '@unifyapps/network/react-query';
import type { ErrorType } from '@unifyapps/network/utils/fetch';
import { useUserStore } from '../../../auth/UserStoreProvider';
import { DUMMY_INTERFACE_RECORD_ID, NO_INTERFACE_RECORD_ID } from '../../const';
import { InterfaceClient, InterfaceModes } from '../../stores/InterfaceStore/types';
import { interfacePermissionsLookupByQueryRequest } from '../permissions';
import type { UserPermissions, UserPermissionsMap } from '../types';
import { adaptCurrentUserPermissions } from '../utils/adaptUserPermissions';

type Args = {
  mode: InterfaceModes | undefined;
  interfaceId: string | undefined;
  client: InterfaceClient;
};

type Return = {
  permissions: UserPermissionsMap;
  isLoading: boolean;
  error: ErrorType<unknown> | null;
};

function canFetchPermissions({
  mode,
  interfaceId,
  client,
  currentUserDetails,
}: Args & {
  currentUserDetails: CurrentUserDetails | undefined;
}) {
  // userContext.external will be true if calls are made via /api-endpoint/external
  // this endpoint is used in public integrations (delta-matrix) and the user context is not available, these are usually public interfaces
  // eg. shopify integration
  // @ts-expect-error -- backend is not using userContext bean for external users they are simply returning {external: true}, that's why it's not defined in the types
  const isExternalUser = currentUserDetails?.external as boolean | undefined;

  return Boolean(
    !process.env.DELTA_MATRIX &&
      mode === InterfaceModes.RUNNER &&
      currentUserDetails?.user?.id && // call shouldn't go on login page, permissions are only required for logged in users
      !isExternalUser &&
      interfaceId !== DUMMY_INTERFACE_RECORD_ID &&
      interfaceId !== NO_INTERFACE_RECORD_ID &&
      client !== InterfaceClient.UA_PLATFORM,
  );
}

export async function fetchInterfacePermissions(
  queryClient: QueryClient,
  {
    mode,
    interfaceId,
    client,
    currentUserDetails,
  }: Args & {
    currentUserDetails: CurrentUserDetails | undefined;
  },
) {
  if (!canFetchPermissions({ mode, interfaceId, client, currentUserDetails })) {
    return {} as UserPermissionsMap;
  }

  await prefetchLookup(queryClient, interfacePermissionsLookupByQueryRequest);
  const data = queryClient.getQueryData<{ response?: { objects?: UserPermissions } }>(
    getLookupQueryKey(interfacePermissionsLookupByQueryRequest),
  );
  return adaptCurrentUserPermissions(data?.response?.objects ?? []);
}

export const useFetchInterfacePermissions = ({ mode, interfaceId, client }: Args): Return => {
  const currentUserDetails = useUserStore().hooks.useCurrentUserDetails();

  const { data, isLoading, error } = useLookup<{ response?: { objects?: UserPermissions } }>(
    interfacePermissionsLookupByQueryRequest,
    {
      query: {
        enabled: canFetchPermissions({ mode, interfaceId, client, currentUserDetails }),
        staleTime: Infinity,
      },
    },
  );

  const adaptedPermissions = adaptCurrentUserPermissions(data?.response?.objects ?? []);

  return { permissions: adaptedPermissions, isLoading, error };
};
